import { Link, Outlet, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Phone, MapPin, Mail } from "lucide-react";
import logo from "@/assets/logo-main.png";

const Layout = () => {
  const location = useLocation();
  
  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="border-b border-border bg-black/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <img src={logo} alt="Battle Born Protection Films" className="h-[60px] w-auto" width="129" height="60" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <Link 
                to="/" 
                className={`text-sm font-medium transition-colors hover:text-accent ${
                  isActive("/") && location.pathname === "/" ? "text-accent" : "text-foreground"
                }`}
              >
                Home
              </Link>
              <Link 
                to="/services" 
                className={`text-sm font-medium transition-colors hover:text-accent ${
                  isActive("/services") ? "text-accent" : "text-foreground"
                }`}
              >
                Services
              </Link>
              <Link 
                to="/gallery" 
                className={`text-sm font-medium transition-colors hover:text-accent ${
                  isActive("/gallery") ? "text-accent" : "text-foreground"
                }`}
              >
                Gallery
              </Link>
              <Link 
                to="/resources" 
                className={`text-sm font-medium transition-colors hover:text-accent ${
                  isActive("/resources") ? "text-accent" : "text-foreground"
                }`}
              >
                Resources
              </Link>
              <Link 
                to="/about" 
                className={`text-sm font-medium transition-colors hover:text-accent ${
                  isActive("/about") ? "text-accent" : "text-foreground"
                }`}
              >
                About
              </Link>
              <Button asChild variant="premium" size="sm">
                <Link to="/contact">Request Free Quote</Link>
              </Button>
            </nav>

            {/* Mobile CTA */}
            <div className="md:hidden">
              <Button asChild variant="premium" size="sm">
                <Link to="/contact">Get Quote</Link>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-black/80 border-t border-border">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div>
              <h3 className="text-lg font-heading font-semibold mb-4 text-accent">Contact Us</h3>
              <div className="space-y-3 text-sm text-muted-foreground">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 text-accent flex-shrink-0" />
                  <span>Battle Born Protection Films<br />6960 W Russell Rd B<br />Las Vegas, NV 89113</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-accent" />
                  <a href="tel:+17025599365" className="hover:text-accent transition-colors">
                    (702) 559-9365
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-accent" />
                  <a href="mailto:battlebornppf@gmail.com" className="hover:text-accent transition-colors">
                    battlebornppf@gmail.com
                  </a>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-heading font-semibold mb-4 text-accent">Our Services</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <Link to="/services/paint-protection-film" className="hover:text-accent transition-colors">
                    Paint Protection Film
                  </Link>
                </li>
                <li>
                  <Link to="/services/ceramic-coatings" className="hover:text-accent transition-colors">
                    Ceramic Coatings
                  </Link>
                </li>
              <li>
                <Link to="/services/window-tint" className="hover:text-accent transition-colors">
                  Ceramic Window Tint
                </Link>
              </li>
              <li>
                <Link to="/services/vinyl-wraps" className="hover:text-accent transition-colors">
                  Vinyl Wraps & Graphics
                </Link>
              </li>
              <li>
                <Link to="/services/windshield-protection" className="hover:text-accent transition-colors">
                  Windshield Protection
                </Link>
              </li>
              </ul>
            </div>

            {/* Hours */}
            <div>
              <h3 className="text-lg font-heading font-semibold mb-4 text-accent">Hours of Operation</h3>
              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex justify-between">
                  <span>Mon–Fri:</span>
                  <span>9:00 AM – 5:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Sat:</span>
                  <span>10:00 AM – 3:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Sun:</span>
                  <span>By Appointment Only</span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-border mt-8 pt-8 text-center text-sm text-muted-foreground">
            <p>&copy; {new Date().getFullYear()} Battle Born Protection Films. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
